import { db as activeDb, META, type KaiDB, type LocalHousehold, type OutboxOp } from '@/db/db';
import { fromEventRecord, toEventRecord, toBabyRecord, type LocalBaby } from '@/lib/schemas';
import { recordFromWire, parsePage, parseResult, wireOperation, ScopeChanged, type Scope, type SyncTransport, type WireRecord } from './protocol';
import { purgeBaby } from '@/privacy/purge';

const tables = (store: KaiDB) => [store.events, store.babies, store.outbox, store.meta, store.importBatches, store.predictionLog];
export const cursorKey = (scope: Scope) => `${META.syncCursorPrefix}${scope.userId}:${scope.householdId}`;
export const stateKey = (scope: Scope) => `syncState:${scope.userId}:${scope.householdId}`;
export interface SyncState { phase: 'syncing' | 'idle' | 'error'; error?: string; lastSuccess?: string }
const rejectionCopy: Record<string, string> = {
  active_sleep: 'Another sleep session is already running. Review or end it first.',
  duplicate_source: 'This imported record already exists. Compare it with the remote record.',
  identity_collision: 'This record identifier is already in use. Review the remote household.',
  parent_deleted: 'This baby profile was deleted on another device.',
  baby_has_live_events: 'Another caregiver added records for this baby. Review them before deleting the profile.',
  retired: 'This data was permanently deleted. It cannot be uploaded again.',
};

async function assertScope(store: KaiDB, scope: Scope, lease?: string) {
  if (await store.meta.get('privacyBusy')) throw new ScopeChanged();
  const rows = await store.meta.bulkGet([META.household, META.session, META.authGeneration, 'syncLease']);
  const h = rows[0]?.value as LocalHousehold | undefined;
  if (!h?.cloud || h.id !== scope.householdId || h.accountId !== scope.userId
    || (rows[1]?.value as { userId?: string } | undefined)?.userId !== scope.userId
    || (rows[2]?.value ?? 0) !== scope.generation || (lease && (rows[3]?.value as { owner?: string } | undefined)?.owner !== lease)) throw new ScopeChanged();
}
async function putRemote(store: KaiDB, record: WireRecord, state: 'synced' | 'local' = 'synced') {
  if ('type' in record) await store.events.put(fromEventRecord(record, state));
  else await store.babies.put({ ...record, sync_state: state });
}
async function localRecord(store: KaiDB, entity: OutboxOp['entity'], id: string) {
  return entity === 'event' ? store.events.get(id) : store.babies.get(id);
}

export class SyncEngine {
  constructor(private transport: SyncTransport, private store: KaiDB = activeDb,
    private clock = () => Date.now(), private random = Math.random) {}

  async cycle(scope: Scope): Promise<boolean> {
    const store = this.store; const lease = crypto.randomUUID();
    const acquired = await store.transaction('rw', store.meta, async () => {
      await assertScope(store, scope);
      const existing = (await store.meta.get('syncLease'))?.value as { expires: number } | undefined;
      if (existing && existing.expires > this.clock() && existing.expires <= this.clock() + 60_000) return false;
      await store.meta.put({ key: 'syncLease', value: { owner: lease, expires: this.clock() + 60_000 } }); return true;
    });
    if (!acquired) return false;
    let active: OutboxOp | undefined;
    try {
      await store.transaction('rw', tables(store), async () => {
        await assertScope(store, scope, lease);
        const inflight = await store.outbox.where('household_id').equals(scope.householdId).filter(o => o.status === 'inflight').toArray();
        for (const op of inflight) await store.outbox.update(op.seq!, { status: 'pending' });
        await store.meta.put({ key: stateKey(scope), value: { phase: 'syncing' } });
      });
      for (let n = 0; n < 100; n++) {
        active = await store.transaction('rw', tables(store), async () => {
          await assertScope(store, scope, lease);
          await store.meta.put({ key: 'syncLease', value: { owner: lease, expires: this.clock() + 60_000 } });
          const ops = await store.outbox.where('household_id').equals(scope.householdId).sortBy('seq');
          const heads = new Map<string, OutboxOp>();
          for (const op of ops) if (!heads.has(`${op.entity}:${op.entity_id}`)) heads.set(`${op.entity}:${op.entity_id}`, op);
          const candidate = [...heads.values()].find(o => o.status === 'pending' && o.next_attempt_at <= this.clock()
            && (o.entity !== 'baby' || o.op !== 'delete' || !ops.some(child => child.entity === 'event' && 'baby_id' in child.data && child.data.baby_id === o.entity_id))
            && (o.entity === 'baby' || o.op === 'delete' || !('baby_id' in o.data) || !ops.some(parent => parent.entity === 'baby' && parent.op === 'create' && parent.entity_id === (o.data as { baby_id: string }).baby_id)));
          if (!candidate) return undefined;
          await store.outbox.update(candidate.seq!, { status: 'inflight', attempted: true });
          const local = await localRecord(store, candidate.entity, candidate.entity_id);
          if (local) {
            if (candidate.entity === 'event') await store.events.update(local.id, { sync_state: 'syncing' });
            else await store.babies.update(local.id, { sync_state: 'syncing' });
          }
          return { ...candidate, attempted: true, status: 'inflight' as const };
        });
        if (!active) break;
        const op = active;
        let result;
        try { result = parseResult(await this.transport.apply(scope.householdId, [wireOperation(op)]), op); }
        catch (error) {
          const code = (error as { code?: string })?.code ?? '';
          if (!(code === 'P0001' || code.startsWith('22') || code.startsWith('23'))) throw error;
          // Invalid/deleted dependencies and malformed old outboxes need review,
          // not unlimited retries that starve independent records.
          result = { operation_id: op.operation_id, status: 'rejected' as const, version: 0, server_record: null,
            reason: error instanceof Error ? error.message : 'This record could not be validated.' };
        }
        await store.transaction('rw', tables(store), async () => {
          await assertScope(store, scope, lease);
          const current = await store.outbox.get(op.seq!);
          if (!current || current.operation_id !== op.operation_id) throw new ScopeChanged();
          const draft = await localRecord(store, op.entity, op.entity_id);
          if (result.status === 'applied') {
            await store.outbox.delete(op.seq!);
            const later = await store.outbox.where('entity_id').equals(op.entity_id).sortBy('seq');
            if (!later.length) await putRemote(store, result.server_record!);
            else if (draft) {
              const rec = 'type' in draft ? toEventRecord(draft) : toBabyRecord(draft as LocalBaby);
              const server = result.server_record!;
              // Preserve newer local fields; install only authoritative version/creation metadata.
              await putRemote(store, { ...rec, version: result.version, created_at: server.created_at,
                ...('created_by' in server ? { created_by: server.created_by } : {}) }, 'local');
            }
          } else {
            const error = result.status === 'conflict' ? 'Remote version changed. Review this draft.' : rejectionCopy[result.reason ?? ''] ?? `This draft needs review: ${result.reason ?? 'record collision'}.`;
            await store.outbox.update(op.seq!, { status: result.status === 'conflict' ? 'conflict' : 'error', server_record: result.server_record, last_error: error, rejection_reason: result.status === 'rejected' ? result.reason : null });
            if (draft) {
              if (op.entity === 'event') await store.events.update(draft.id, { sync_state: result.status === 'conflict' ? 'conflict' : 'error', sync_error: error });
              else await store.babies.update(draft.id, { sync_state: result.status === 'conflict' ? 'conflict' : 'error', sync_error: error });
            }
          }
        });
        active = undefined;
      }
      let more = false;
      for (let n = 0; n < 50; n++) {
        await store.transaction('rw', store.meta, async () => {
          await assertScope(store, scope, lease);
          await store.meta.put({ key: 'syncLease', value: { owner: lease, expires: this.clock() + 60_000 } });
        });
        const cursor = ((await store.meta.get(cursorKey(scope)))?.value as string | undefined) ?? '0';
        const page = parsePage(await this.transport.pull(scope.householdId, cursor), scope.householdId, cursor);
        await store.transaction('rw', tables(store), async () => {
          await assertScope(store, scope, lease);
          const old = ((await store.meta.get(cursorKey(scope)))?.value as string | undefined) ?? '0';
          if (old !== cursor) throw new ScopeChanged();
          const retired = new Set(page.retired_baby_ids ?? []);
          for (const id of retired) await purgeBaby(store, scope.householdId, id, true);
          for (const row of page.changes) {
            if (retired.has(row.entity_type === 'baby' ? row.entity_id : (row.record as {baby_id: string}).baby_id)) continue;
            const local = await localRecord(store, row.entity_type, row.entity_id);
            const pending = await store.outbox.where('entity_id').equals(row.entity_id).sortBy('seq');
            if (pending.length) {
              // A remote update never overwrites an unsent draft or its immutable attempt.
              for (const op of pending) {
                const prior = op.server_record;
                if (!prior || prior.version < row.record.version) await store.outbox.update(op.seq!, { server_record: row.record });
              }
            } else if (!local || local.version < row.record.version) await putRemote(store, row.record);
          }
          await store.meta.put({ key: cursorKey(scope), value: page.next_cursor });
          if (!page.has_more) {
            const h = (await store.meta.get(META.household))!.value as LocalHousehold;
            if (!h.syncReady) await store.meta.put({ key: META.household, value: { ...h, syncReady: true } });
          }
        });
        more = page.has_more;
        if (!more) break;
      }
      await store.transaction('rw', store.meta, async () => {
        await assertScope(store, scope, lease);
        await store.meta.put({ key: stateKey(scope), value: { phase: 'idle', lastSuccess: new Date(this.clock()).toISOString() } satisfies SyncState });
      });
      return more;
    } catch (error) {
      if (error instanceof ScopeChanged) throw error;
      const message = error instanceof Error ? error.message : 'Synchronization failed.';
      await store.transaction('rw', tables(store), async () => {
        await assertScope(store, scope, lease);
        if (active) {
          const current = await store.outbox.get(active.seq!);
          if (current?.operation_id === active.operation_id && current.status === 'inflight') {
            const count = current.retry_count + 1;
            await store.outbox.update(current.seq!, { status: 'pending', retry_count: count, last_error: message,
              next_attempt_at: this.clock() + Math.min(300_000, 1000 * 2 ** Math.min(count, 8)) * (0.5 + this.random()) });
          }
        }
        await store.meta.put({ key: stateKey(scope), value: { phase: 'error', error: message } satisfies SyncState });
      });
      throw error;
    } finally {
      await store.transaction('rw', store.meta, async () => {
        const held = (await store.meta.get('syncLease'))?.value as { owner?: string } | undefined;
        if (held?.owner === lease) await store.meta.delete('syncLease');
      });
    }
  }
}

/** Deliberate manual retry changes scheduling, never the attempted wire request. */
export async function retryPending(scope: Scope) {
  await activeDb.transaction('rw', activeDb.meta, activeDb.outbox, async () => {
    await assertScope(activeDb, scope);
    await activeDb.outbox.where('household_id').equals(scope.householdId).filter(o => o.status === 'pending').modify({ next_attempt_at: 0 });
  });
}

/** Explicit resolution keeps the latest local draft or the reviewed remote value. */
export async function resolveDraft(scope: Scope, seq: number, choice: 'local' | 'remote', reviewedRemoteVersion?: number) {
  await activeDb.transaction('rw', tables(activeDb), async () => {
    await assertScope(activeDb, scope);
    const op = await activeDb.outbox.get(seq);
    if (!op || op.household_id !== scope.householdId || !['conflict', 'error'].includes(op.status)) throw new Error('This draft is no longer awaiting resolution.');
    const related = await activeDb.outbox.where('entity_id').equals(op.entity_id).sortBy('seq');
    if (related[0]?.operation_id !== op.operation_id) throw new Error('Resolve the earlier operation first.');
    const draft = await localRecord(activeDb, op.entity, op.entity_id);
    const remote = op.server_record;
    if (choice === 'local' && op.rejection_reason === 'retired') throw new Error('Permanently deleted data cannot be uploaded again.');
    if (choice === 'local' && !remote && ['identity_collision', 'duplicate_source', 'parent_deleted'].includes(op.rejection_reason ?? '')) throw new Error('This draft cannot be resent unchanged. It remains saved on this device until you resolve its dependency or discard it.');
    if (reviewedRemoteVersion !== undefined && reviewedRemoteVersion !== (remote?.version ?? 0)) throw new Error('The remote record changed. Review it again before choosing.');
    if (choice === 'remote' && !remote && op.entity === 'baby' && (await activeDb.events.where('baby_id').equals(op.entity_id).count())) throw new Error('Resolve this baby’s dependent event drafts first.');
    await activeDb.outbox.bulkDelete(related.map(o => o.seq!));
    if (choice === 'remote') {
      if (remote) await putRemote(activeDb, remote);
      else if (op.entity === 'event') await activeDb.events.delete(op.entity_id);
      else await activeDb.babies.delete(op.entity_id);
    } else {
      if (!draft) throw new Error('Local draft is unavailable.');
      const record = 'type' in draft ? toEventRecord(draft) : toBabyRecord(draft as LocalBaby);
      record.version = remote?.version ?? (op.status === 'error' && op.op !== 'create' ? op.base_version : 0);
      if (!record.version && record.deleted_at) throw new Error('A deleted local draft cannot create a new record. Use the remote option to discard it.');
      if (remote) record.created_at = remote.created_at;
      await putRemote(activeDb, record, 'local');
      await activeDb.outbox.add({ ...op, seq: undefined, operation_id: crypto.randomUUID(), op: record.version ? record.deleted_at ? 'delete' : 'update' : 'create',
        data: record, base_version: record.version, client_ts: new Date().toISOString(), attempted: false, retry_count: 0,
        status: 'pending', next_attempt_at: 0, last_error: null, server_record: null, rejection_reason: null });
    }
  });
}

// Used by tests and adoption to compare a proposed record against its baseline.
export function normalizedServerRecord(entity: OutboxOp['entity'], record: unknown, householdId: string) {
  return recordFromWire(entity, record, householdId);
}
